import React, { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { db, deleteUser } from "../../config/firebase";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Box,
  TextField,
  Divider,
  Snackbar,
  Alert,
  Button,
  Container,
  Chip,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  Dialog,
  DialogContent,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChalkboardTeacher,
  faPenToSquare,
  faArchive,
  faFileExcel,
  faTable,
  faKey,
  faArrowRotateLeft,
  faUserSlash,
  faEnvelope,
  faXmark,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import EmailUpdateDialog from "./EmailUpdateDialog";
import AddTpInstructor from "./AddTpInstructor";
import EditTpInstructor from "./EditTpInstructor";
import EditArchivedInstructor from "./EditArchivedInstructor";
import TpUserTypes from "./TpUserTypes";
import TpUserMgtBreadCrumbs from "../Layout/TpUserMgtBreadCrumbs";
import InstructorBulkUpload from "./InstructorBulkUpload";
import ConfirmDeleteDialog from "../common/ConfirmDeleteDialog";
import ModalDialogTitle from "../common/ModalDialogTitle";
import ModalActions from "../common/ModalActions";
import InstructorWelcomeEmailEditor from "./InstructorWelcomeEmailEditor";
import useInstructorWelcomeEmail from "./hooks/useInstructorWelcomeEmail";
import { WELCOME_COLLECTION, WELCOME_DOC_ID } from "../../utils/instructorWelcome";
import { splitName } from "../../utils/name";
import { isInstructorInactive } from "../../utils/instructorStatus";

// Cards/table are sorted by last name — most instructor docs have a
// separate lastName field, but older records only have the combined
// "name" string, so fall back to splitting that.
const getLastName = (user) => user.lastName || splitName(user.name).lastName || "";

const TpInstructors = () => {
  const [instructors, setInstructors] = useState([]);
  const [inactiveInstructors, setInactiveInstructors] = useState([]);
  const [archivedInstructors, setArchivedInstructors] = useState([]);
  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [pathways, setPathways] = useState([]);
  const [pathwaysLoading, setPathwaysLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [selectedPartner, setSelectedPartner] = useState("");
  const [selectedPathwayId, setSelectedPathwayId] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [userTypesModalOpen, setUserTypesModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isTableView, setIsTableView] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTargetInstructor, setEmailTargetInstructor] = useState(null);
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [welcomeTarget, setWelcomeTarget] = useState(null);
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [welcomeError, setWelcomeError] = useState("");
  const [resettingPasswordFor, setResettingPasswordFor] = useState(null);
  const { send: sendWelcomeEmail } = useInstructorWelcomeEmail();

  const fetchInstructors = async () => {
    try {
      setLoading(true);

      // Fetch instructors from users collection
      const usersSnapshot = await getDocs(collection(db, "users"));
      const instructorUsers = usersSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((user) => user.userTypes?.instructor === true)
        .sort((a, b) => getLastName(a).localeCompare(getLastName(b)));

      // The Active switch on EditTpInstructor writes "-1" (active) or "0"
      // (inactive) to the Active field; treat a missing field as active so
      // existing instructors created before this field existed still show
      // up under Active Instructors.
      const activeInstructorUsers = instructorUsers.filter(
        (user) => !isInstructorInactive(user),
      );
      const inactiveInstructorUsers = instructorUsers.filter((user) =>
        isInstructorInactive(user),
      );

      // Fetch Sites for filtering dropdown
      try {
        setSitesLoading(true);
        const sitesSnapshot = await getDocs(collection(db, "Sites"));
        const sitesData = sitesSnapshot.docs.map((doc) => ({
          id: doc.id,
          SiteID: doc.data().SiteID,
          SiteName: doc.data().SiteName,
          ...doc.data(),
        }));
        // sort by SiteName
        sitesData.sort((a, b) =>
          (a.SiteName || "").localeCompare(b.SiteName || ""),
        );
        setSites(sitesData);
      } catch (e) {
        console.error("Error fetching Sites for instructor filter:", e);
        setSites([]);
      } finally {
        setSitesLoading(false);
      }

      // Fetch PathwaysPrograms for Pathway filter
      try {
        setPathwaysLoading(true);
        const pwSnap = await getDocs(collection(db, "PathwaysPrograms"));
        const pwData = pwSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        pwData.sort((a, b) =>
          (a.PathwayName || "").localeCompare(b.PathwayName || ""),
        );
        setPathways(pwData);
      } catch (e) {
        console.error("Error fetching PathwaysPrograms for filters:", e);
        setPathways([]);
      } finally {
        setPathwaysLoading(false);
      }

      // Fetch delivery details
      const deliveryDetailsSnapshot = await getDocs(
        collection(db, "DeliveryDetails"),
      );
      const deliveryData = deliveryDetailsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch archived instructors
      const archivedSnapshot = await getDocs(
        collection(db, "InstructorArchive"),
      );
      const archivedData = archivedSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => getLastName(a).localeCompare(getLastName(b)));

      setInstructors(activeInstructorUsers);
      setInactiveInstructors(inactiveInstructorUsers);
      setDeliveryDetails(deliveryData);
      setArchivedInstructors(archivedData);
    } catch (error) {
      console.error("Error fetching instructors: ", error);
      setInstructors([]);
      setInactiveInstructors([]);
      setArchivedInstructors([]);
      setDeliveryDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstructors();
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Each filter narrows the other two, so when one changes, drop any other
  // pick that no instructor on the current tab still matches — otherwise a
  // dropdown would hold a value it no longer offers and show zero results.
  const handleSiteSelectChange = (event) => {
    const siteId = event.target.value;
    setSelectedSiteId(siteId);
    const pool = tabFilterPool.filter((u) => matchesSite(u, siteId));
    if (selectedPartner && !pool.some((u) => matchesPartner(u))) {
      setSelectedPartner("");
    }
    if (selectedPathwayId && !pool.some((u) => matchesPathway(u))) {
      setSelectedPathwayId("");
    }
  };

  const handlePartnerSelectChange = (event) => {
    const partner = event.target.value;
    setSelectedPartner(partner);
    const pool = tabFilterPool.filter((u) => matchesPartner(u, partner));
    if (selectedSiteId && !pool.some((u) => matchesSite(u))) {
      setSelectedSiteId("");
    }
    if (selectedPathwayId && !pool.some((u) => matchesPathway(u))) {
      setSelectedPathwayId("");
    }
  };

  const handlePathwaySelectChange = (event) => {
    const pathwayId = event.target.value;
    setSelectedPathwayId(pathwayId);
    const pool = tabFilterPool.filter((u) => matchesPathway(u, pathwayId));
    if (selectedSiteId && !pool.some((u) => matchesSite(u))) {
      setSelectedSiteId("");
    }
    if (selectedPartner && !pool.some((u) => matchesPartner(u))) {
      setSelectedPartner("");
    }
  };

  const handleEditClick = (instructor) => {
    // Find delivery details for this instructor
    const instructorDeliveryDetails = deliveryDetails.filter(
      (detail) => detail.InstructorID === instructor.id,
    );

    setSelectedInstructor({
      ...instructor,
      deliveryDetails: instructorDeliveryDetails,
    });
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedInstructor(null);
  };

  const handleEditSuccess = () => {
    fetchInstructors();
    setSnackbarMessage("Instructor has been updated");
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedForDelete([]);
  };

  const toggleTableView = () => setIsTableView((v) => !v);

  const toggleSelectInstructor = (id) => {
    setSelectedForDelete((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAllInstructors = () => {
    setSelectedForDelete((prev) =>
      prev.length === furtherFilteredInstructors.length
        ? []
        : furtherFilteredInstructors.map((i) => i.id),
    );
  };

  const handleOpenBulkDeleteConfirm = () => {
    if (selectedForDelete.length === 0) return;
    setBulkDeleteConfirmOpen(true);
  };

  // Temporary bulk-delete tool: removes selected active instructors from
  // both Firebase Auth and the users collection via the deleteUser callable
  // (unlike the single-instructor delete in EditTpInstructor, which only
  // removes the Firestore doc and leaves the Auth account behind).
  const handleBulkDeleteConfirmed = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of selectedForDelete) {
      const result = await deleteUser(id);
      if (result.success) {
        successCount += 1;
      } else {
        failCount += 1;
      }
    }

    setBulkDeleting(false);
    setBulkDeleteConfirmOpen(false);
    setSelectedForDelete([]);
    fetchInstructors();
    setSnackbarMessage(
      failCount > 0
        ? `Deleted ${successCount} instructor${successCount === 1 ? "" : "s"}, ${failCount} failed to delete`
        : `Deleted ${successCount} instructor${successCount === 1 ? "" : "s"}`,
    );
    setSnackbarOpen(true);
  };

  const exportInstructorsCSV = (rows, filenamePrefix = "instructors") => {
    if (!rows || !rows.length) {
      const csvEmpty = "No data to export";
      const blobEmpty = new Blob([csvEmpty], { type: "text/plain" });
      const urlEmpty = URL.createObjectURL(blobEmpty);
      const aEmpty = document.createElement("a");
      aEmpty.href = urlEmpty;
      aEmpty.download = `${filenamePrefix}_empty.txt`;
      aEmpty.click();
      URL.revokeObjectURL(urlEmpty);
      return;
    }

    const escape = (val) => {
      if (val === null || val === undefined) return "";
      const s = String(val);
      // escape quotes by doubling them
      return `"${s.replace(/"/g, '""')}"`;
    };

    const headers = ["ID", "Name", "Email", "Phone", "Site", "User Types"];
    const csvRows = [headers.map(escape).join(",")];

    rows.forEach((r) => {
      const row = [
        r.id || "",
        r.name || "",
        r.email || r.InstWorkEmail || "",
        r.phone || "",
        getSiteNameForInstructor(r) || "",
        getUserTypeLabels(r).join("; ") || "",
      ];
      csvRows.push(row.map(escape).join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    a.download = `${filenamePrefix}_${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleResetFilters = () => {
    setSelectedSiteId("");
    setSelectedPartner("");
    setSelectedPathwayId("");
    setSearchTerm("");
  };

  const filterUsers = (users) => {
    return users.filter(
      (user) =>
        (user.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || "").toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  // Site / Partner / Pathway as stored on an instructor. EditTpInstructor
  // writes SOD_Site_ID, Partner (the partner name) and Pathway (PathwayID);
  // the other site keys and the DeliveryDetails pathway fields cover older
  // records that predate those fields.
  const getUserSiteId = (user) =>
    String(
      user.SOD_Site_ID ??
        user.SOD_SiteId ??
        user.siteId ??
        user.site_of_delivery ??
        user.SiteID ??
        user.siteOfDelivery ??
        "",
    );

  const getUserPartner = (user) => String(user.Partner || user.partner || "");

  const deliveryPathwaysByInstructor = {};
  deliveryDetails.forEach((d) => {
    if (!d.InstructorID) return;
    if (!deliveryPathwaysByInstructor[d.InstructorID]) {
      deliveryPathwaysByInstructor[d.InstructorID] = new Set();
    }
    const ids = deliveryPathwaysByInstructor[d.InstructorID];
    [d.TP_Pathway, d.tp_pathway, d.tp_pathway_id, d.PathwayID].forEach((v) => {
      if (v) ids.add(String(v));
    });
  });

  const getUserPathwayIds = (user) => {
    const ids = new Set(deliveryPathwaysByInstructor[user.id] || []);
    const own = user.Pathway || user.pathway;
    if (own) ids.add(String(own));
    return ids;
  };

  const matchesSite = (user, siteId = selectedSiteId) =>
    !siteId || getUserSiteId(user) === String(siteId);

  const matchesPartner = (user, partner = selectedPartner) =>
    !partner || getUserPartner(user) === String(partner);

  const matchesPathway = (user, pathwayId = selectedPathwayId) =>
    !pathwayId || getUserPathwayIds(user).has(String(pathwayId));

  const applyFilters = (userList) =>
    filterUsers(userList).filter(
      (user) => matchesSite(user) && matchesPartner(user) && matchesPathway(user),
    );

  const furtherFilteredInstructors = applyFilters(instructors);
  const furtherFilteredInactive = applyFilters(inactiveInstructors);
  const furtherFilteredArchived = applyFilters(archivedInstructors);

  // Dropdown options only list values that at least one instructor on the
  // current tab has, given the other two selections — so picking a Site
  // narrows Partner and Pathway to what's actually at that site, and so on.
  // The current selection is always kept so the Select never shows blank.
  const tabFilterPool =
    tabValue === 1
      ? inactiveInstructors
      : tabValue === 2
        ? archivedInstructors
        : instructors;

  const siteFilterOptions = sites.filter((site) => {
    const id = String(site.SiteID || site.id);
    return (
      id === String(selectedSiteId) ||
      tabFilterPool.some(
        (u) =>
          getUserSiteId(u) === id && matchesPartner(u) && matchesPathway(u),
      )
    );
  });

  const partnerFilterOptions = Array.from(
    new Set(
      [
        selectedPartner,
        ...tabFilterPool
          .filter((u) => matchesSite(u) && matchesPathway(u))
          .map(getUserPartner),
      ].filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const pathwayFilterOptions = pathways.filter((pw) => {
    const id = String(pw.PathwayID || pw.id);
    return (
      id === String(selectedPathwayId) ||
      tabFilterPool.some(
        (u) =>
          getUserPathwayIds(u).has(id) && matchesSite(u) && matchesPartner(u),
      )
    );
  });

  const renderFilterSelects = () => (
    <>
      <Box>
        <Select
          value={selectedSiteId}
          onChange={handleSiteSelectChange}
          displayEmpty
          size="small"
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All Sites</MenuItem>
          {sitesLoading ? (
            <MenuItem disabled>Loading sites...</MenuItem>
          ) : (
            siteFilterOptions.map((s) => (
              <MenuItem key={s.id} value={s.SiteID || s.id}>
                {s.SiteName || s.SiteID}
              </MenuItem>
            ))
          )}
        </Select>
      </Box>

      {/* Partner filter */}
      <Box>
        <Select
          value={selectedPartner}
          onChange={handlePartnerSelectChange}
          displayEmpty
          size="small"
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All Partners</MenuItem>
          {partnerFilterOptions.map((partner) => (
            <MenuItem key={partner} value={partner}>
              {partner}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Pathway filter */}
      <Box>
        <Select
          value={selectedPathwayId}
          onChange={handlePathwaySelectChange}
          displayEmpty
          size="small"
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All Pathways</MenuItem>
          {pathwaysLoading ? (
            <MenuItem disabled>Loading pathways...</MenuItem>
          ) : (
            pathwayFilterOptions.map((pw) => (
              <MenuItem key={pw.id} value={pw.PathwayID || pw.id}>
                {pw.PathwayName || pw.PathwayID || pw.id}
              </MenuItem>
            ))
          )}
        </Select>
      </Box>
    </>
  );

  const getUserTypeLabels = (user) => {
    const types = [];
    if (user.techPrepAdminFlag || user.userTypes?.admin) types.push("Admin");
    if (user.userTypes?.coordinator) types.push("Coordinator");
    if (user.userTypes?.instructor) types.push("Instructor");
    if (user.userTypes?.staff) types.push("Staff");
    if (user.userTypes?.pathwayManager) types.push("Pathway Manager");
    return types;
  };

  const getSiteNameForInstructor = (instructor) => {
    const candidate =
      instructor.SOD_Site_ID ??
      instructor.SOD_SiteId ??
      instructor.siteId ??
      instructor.site_of_delivery ??
      instructor.SiteID ??
      instructor.siteOfDelivery ??
      "";

    if (!candidate) return "- No Site";

    const match = sites.find((s) => {
      // Compare against SiteID or document id
      return (
        String(s.SiteID || "").trim() === String(candidate).trim() ||
        String(s.id || "").trim() === String(candidate).trim()
      );
    });

    return match ? match.SiteName || match.SiteID || "- No Site" : "- No Site";
  };

  // The address on file, matching what the card displays.
  const getInstructorEmail = (instructor) =>
    instructor?.email || instructor?.InstWorkEmail || "";

  const handlePasswordReset = async (instructor) => {
    const email = getInstructorEmail(instructor);
    if (!email) {
      setSnackbarMessage("This instructor has no email on file.");
      setSnackbarOpen(true);
      return;
    }
    setResettingPasswordFor(instructor.id);
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      setSnackbarMessage(`Password reset email sent to ${email}`);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setSnackbarMessage(
        error.message || "Error sending password reset email",
      );
    } finally {
      setResettingPasswordFor(null);
      setSnackbarOpen(true);
    }
  };

  const handleWelcomeClick = (instructor) => {
    setWelcomeError("");
    setWelcomeTarget(instructor);
  };

  const handleWelcomeClose = () => {
    if (welcomeSending) return;
    setWelcomeTarget(null);
  };

  const handleWelcomeConfirm = async () => {
    const toEmail = getInstructorEmail(welcomeTarget);
    if (!toEmail) {
      setWelcomeError("This instructor has no email address on file.");
      return;
    }

    setWelcomeSending(true);
    setWelcomeError("");
    try {
      // Read the saved email at send time so an edit made a moment ago is used.
      const snap = await getDoc(doc(db, WELCOME_COLLECTION, WELCOME_DOC_ID));
      const template = snap.exists() ? snap.data() : null;
      if (!template || !(template.body || "").trim()) {
        setWelcomeError(
          "No welcome email has been saved yet. Write and save one on the Welcome Email tab first.",
        );
        return;
      }

      const result = await sendWelcomeEmail({
        toEmail,
        instructorName: welcomeTarget.name || "",
        subject: template.subject || "",
        body: template.body,
      });

      setWelcomeTarget(null);
      setSnackbarMessage(result.message || "Welcome email sent");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error sending welcome email:", error);
      setWelcomeError(error.message || "The welcome email could not be sent.");
    } finally {
      setWelcomeSending(false);
    }
  };

  const handleEmailKeyClick = (instructor) => {
    setEmailTargetInstructor(instructor);
    setEmailDialogOpen(true);
  };

  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setEmailTargetInstructor(null);
  };

  const handleEmailUpdateSuccess = () => {
    handleEmailDialogClose();
    fetchInstructors();
    setSnackbarMessage("Instructor email updated");
    setSnackbarOpen(true);
  };

  const renderInstructorCard = (
    instructor,
    isArchived = false,
    showCheckbox = !isArchived,
  ) => (
    <Grid item xs={12} sm={6} md={4} key={instructor.id}>
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {showCheckbox && (
              <Grid item>
                <Checkbox
                  checked={selectedForDelete.includes(instructor.id)}
                  onChange={() => toggleSelectInstructor(instructor.id)}
                />
              </Grid>
            )}
            <Grid item xs>
              <Typography variant="h6" component="div">
                {instructor.name || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {instructor.email || instructor.InstWorkEmail || "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {instructor.phone || "N/A"}
              </Typography>

              {/* Labeled Site / Partner / Pathway display */}
              <Box mt={1}>
                <Typography
                  variant="caption"
                  color="common.black"
                  display="block"
                  sx={{ fontWeight: "bold" }}
                >
                  SITE
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getSiteNameForInstructor(instructor)}
                </Typography>

                <Typography
                  variant="caption"
                  color="common.black"
                  display="block"
                  sx={{ mt: 0.5, fontWeight: "bold" }}
                >
                  PARTNER
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {instructor.Partner || instructor.partner || "-"}
                </Typography>

                <Typography
                  variant="caption"
                  color="common.black"
                  display="block"
                  sx={{ mt: 0.5, fontWeight: "bold" }}
                >
                  PATHWAY
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {instructor.Pathway || instructor.pathway || "-"}
                </Typography>
              </Box>

              {/* Display user type chips */}
              <Box mt={1} mb={1}>
                {isArchived ? (
                  <Chip
                    label="Archived"
                    size="small"
                    color="warning"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ) : (
                  getUserTypeLabels(instructor).map((type, index) => (
                    <Chip
                      key={index}
                      label={type}
                      size="small"
                      color={type === "Admin" ? "primary" : "secondary"}
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))
                )}
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <FontAwesomeIcon
                  icon={faPenToSquare}
                  style={{ color: "purple", cursor: "pointer" }}
                  size="2x"
                  onClick={() => handleEditClick(instructor)}
                />

                <FontAwesomeIcon
                  icon={faKey}
                  title="Update email"
                  style={{ color: "#2b7cff", cursor: "pointer" }}
                  size="2x"
                  onClick={() => handleEmailKeyClick(instructor)}
                />

                {!isArchived && (
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    title="Send welcome email"
                    style={{ color: "#2e7d32", cursor: "pointer" }}
                    size="2x"
                    onClick={() => handleWelcomeClick(instructor)}
                  />
                )}

                {!isArchived && (
                  <FontAwesomeIcon
                    icon={faLock}
                    title="Send password reset email"
                    style={{
                      color:
                        resettingPasswordFor === instructor.id
                          ? "#bbb"
                          : "#2b7cff",
                      cursor:
                        resettingPasswordFor === instructor.id
                          ? "default"
                          : "pointer",
                    }}
                    size="2x"
                    onClick={() =>
                      resettingPasswordFor === instructor.id
                        ? null
                        : handlePasswordReset(instructor)
                    }
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ paddingTop: "80px" }}>
      <TpUserMgtBreadCrumbs />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box display="flex" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
          <FontAwesomeIcon
            icon={faChalkboardTeacher}
            style={{
              color: "#4caf50",
              fontSize: "2rem",
              marginRight: "16px",
            }}
          />
          <Typography variant="h4" component="div" gutterBottom>
            Tech Prep Instructors
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <AddTpInstructor onSuccess={fetchInstructors} />
          <InstructorBulkUpload onComplete={fetchInstructors} />
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setUserTypesModalOpen(true)}
          >
            Usertypes
          </Button>
        </Box>
        <TpUserTypes
          open={userTypesModalOpen}
          onClose={() => setUserTypesModalOpen(false)}
        />
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Instructor Tabs"
        >
          <Tab
            label="Active Instructors"
            icon={<FontAwesomeIcon icon={faChalkboardTeacher} />}
          />
          <Tab
            label="Inactive Instructors"
            icon={<FontAwesomeIcon icon={faUserSlash} />}
          />
          <Tab
            label="Instructor Archive"
            icon={<FontAwesomeIcon icon={faArchive} />}
          />
          <Tab
            label="Welcome Email"
            icon={<FontAwesomeIcon icon={faEnvelope} />}
          />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          {/* Count on its own line above filters */}
          <Box mb={2}>
            <Typography variant="h6" color="text.secondary">
              {furtherFilteredInstructors.length} instructor user
              {furtherFilteredInstructors.length !== 1 ? "s" : ""} found
            </Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <Box display="flex" alignItems="center" gap={2}>
              {renderFilterSelects()}

              {/* Reset filters icon (orange) placed to the right of the filters */}
              <Box>
                <IconButton
                  onClick={handleResetFilters}
                  size="small"
                  title="Reset Filters"
                >
                  <FontAwesomeIcon icon={faArrowRotateLeft} color="#ff9800" />
                </IconButton>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                label="Search Instructors"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <IconButton
                onClick={handleClearSearch}
                size="small"
                title="Clear search"
                disabled={!searchTerm}
              >
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
              <IconButton
                title="Export Excel"
                size="small"
                onClick={() =>
                  exportInstructorsCSV(
                    furtherFilteredInstructors,
                    "instructors_active",
                  )
                }
                sx={{ color: "#2e7d32" }}
              >
                <FontAwesomeIcon icon={faFileExcel} />
              </IconButton>
              <IconButton
                title={isTableView ? "Card View" : "Table View"}
                size="small"
                onClick={toggleTableView}
                sx={{ color: "#1976d2" }}
              >
                <FontAwesomeIcon icon={faTable} />
              </IconButton>
            </Box>
          </Box>

          {furtherFilteredInstructors.length > 0 && (
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Box display="flex" alignItems="center">
                <Checkbox
                  size="small"
                  checked={
                    selectedForDelete.length ===
                    furtherFilteredInstructors.length
                  }
                  indeterminate={
                    selectedForDelete.length > 0 &&
                    selectedForDelete.length <
                      furtherFilteredInstructors.length
                  }
                  onChange={toggleSelectAllInstructors}
                />
                <Typography variant="body2">Select All</Typography>
              </Box>
              {selectedForDelete.length > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleOpenBulkDeleteConfirm}
                >
                  Delete Selected ({selectedForDelete.length})
                </Button>
              )}
            </Box>
          )}

          <Divider style={{ marginBottom: "20px" }} />

          {loading ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          ) : isTableView ? (
            <TableContainer component={Paper} sx={{ mb: 6 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          furtherFilteredInstructors.length > 0 &&
                          selectedForDelete.length ===
                            furtherFilteredInstructors.length
                        }
                        indeterminate={
                          selectedForDelete.length > 0 &&
                          selectedForDelete.length <
                            furtherFilteredInstructors.length
                        }
                        onChange={toggleSelectAllInstructors}
                      />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Site</TableCell>
                    <TableCell>User Types</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {furtherFilteredInstructors.map((instructor) => (
                    <TableRow key={instructor.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedForDelete.includes(instructor.id)}
                          onChange={() =>
                            toggleSelectInstructor(instructor.id)
                          }
                        />
                      </TableCell>
                      <TableCell>{instructor.name || "N/A"}</TableCell>
                      <TableCell>{instructor.email || "N/A"}</TableCell>
                      <TableCell>{instructor.phone || "N/A"}</TableCell>
                      <TableCell>
                        {getSiteNameForInstructor(instructor)}
                      </TableCell>
                      <TableCell>
                        {getUserTypeLabels(instructor).join(", ")}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleEditClick(instructor)}
                        >
                          Edit
                        </Button>
                        <IconButton
                          size="small"
                          title="Send welcome email"
                          onClick={() => handleWelcomeClick(instructor)}
                          sx={{ color: "#2e7d32" }}
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                        </IconButton>
                        {/* Avatar/upload removed */}
                      </TableCell>
                    </TableRow>
                  ))}
                  {furtherFilteredInstructors.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No instructor users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Grid container spacing={2} style={{ marginBottom: "50px" }}>
              {furtherFilteredInstructors.map((instructor) =>
                renderInstructorCard(instructor, false),
              )}
              {furtherFilteredInstructors.length === 0 && (
                <Grid item xs={12}>
                  <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="text.secondary">
                      No instructor users found
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}

      {tabValue === 1 && (
        <>
          {/* Inactive header: count + filters + search/actions */}
          <Box mb={2}>
            <Typography variant="h6" color="text.secondary">
              {furtherFilteredInactive.length} inactive instructor
              {furtherFilteredInactive.length !== 1 ? "s" : ""} found
            </Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <Box display="flex" alignItems="center" gap={2}>
              {renderFilterSelects()}

              {/* Reset filters icon for inactive tab filters */}
              <Box>
                <IconButton
                  onClick={handleResetFilters}
                  size="small"
                  title="Reset Filters"
                >
                  <FontAwesomeIcon icon={faArrowRotateLeft} color="#ff9800" />
                </IconButton>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                label="Search Inactive Instructors"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <IconButton
                onClick={handleClearSearch}
                size="small"
                title="Clear search"
                disabled={!searchTerm}
              >
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
              <IconButton
                title="Export Excel"
                size="small"
                onClick={() =>
                  exportInstructorsCSV(
                    furtherFilteredInactive,
                    "instructors_inactive",
                  )
                }
                sx={{ color: "#2e7d32" }}
              >
                <FontAwesomeIcon icon={faFileExcel} />
              </IconButton>
              <IconButton
                title={isTableView ? "Card View" : "Table View"}
                size="small"
                onClick={toggleTableView}
                sx={{ color: "#1976d2" }}
              >
                <FontAwesomeIcon icon={faTable} />
              </IconButton>
            </Box>
          </Box>

          <Divider style={{ marginBottom: "20px" }} />

          {loading ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          ) : isTableView ? (
            <TableContainer component={Paper} sx={{ mb: 6 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Site</TableCell>
                    <TableCell>User Types</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {furtherFilteredInactive.map((instructor) => (
                    <TableRow key={instructor.id} hover>
                      <TableCell>{instructor.name || "N/A"}</TableCell>
                      <TableCell>{instructor.email || "N/A"}</TableCell>
                      <TableCell>{instructor.phone || "N/A"}</TableCell>
                      <TableCell>
                        {getSiteNameForInstructor(instructor)}
                      </TableCell>
                      <TableCell>
                        {getUserTypeLabels(instructor).join(", ")}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleEditClick(instructor)}
                        >
                          Edit
                        </Button>
                        <IconButton
                          size="small"
                          title="Send welcome email"
                          onClick={() => handleWelcomeClick(instructor)}
                          sx={{ color: "#2e7d32" }}
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {furtherFilteredInactive.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No inactive instructors found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Grid container spacing={2} style={{ marginBottom: "50px" }}>
              {furtherFilteredInactive.map((instructor) =>
                renderInstructorCard(instructor, false, false),
              )}
              {furtherFilteredInactive.length === 0 && (
                <Grid item xs={12}>
                  <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="text.secondary">
                      No inactive instructors found
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}

      {tabValue === 2 && (
        <>
          {/* Archived header: count + filters + search/actions */}
          <Box mb={2}>
            <Typography variant="h6" color="text.secondary">
              {furtherFilteredArchived.length} archived instructor
              {furtherFilteredArchived.length !== 1 ? "s" : ""} found
            </Typography>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <Box display="flex" alignItems="center" gap={2}>
              {renderFilterSelects()}

              {/* Reset filters icon for archived tab filters */}
              <Box>
                <IconButton
                  onClick={handleResetFilters}
                  size="small"
                  title="Reset Filters"
                >
                  <FontAwesomeIcon icon={faArrowRotateLeft} color="#ff9800" />
                </IconButton>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                label="Search Archived Instructors"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <IconButton
                onClick={handleClearSearch}
                size="small"
                title="Clear search"
                disabled={!searchTerm}
              >
                <FontAwesomeIcon icon={faXmark} />
              </IconButton>
              <IconButton
                title="Export Excel"
                size="small"
                onClick={() =>
                  exportInstructorsCSV(
                    furtherFilteredArchived,
                    "instructors_archived",
                  )
                }
                sx={{ color: "#2e7d32" }}
              >
                <FontAwesomeIcon icon={faFileExcel} />
              </IconButton>
              <IconButton
                title={isTableView ? "Card View" : "Table View"}
                size="small"
                onClick={toggleTableView}
                sx={{ color: "#1976d2" }}
              >
                <FontAwesomeIcon icon={faTable} />
              </IconButton>
            </Box>
          </Box>

          <Divider style={{ marginBottom: "20px" }} />

          {loading ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          ) : isTableView ? (
            <TableContainer component={Paper} sx={{ mb: 6 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Site</TableCell>
                    <TableCell>User Types</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {furtherFilteredArchived.map((instructor) => (
                    <TableRow key={instructor.id} hover>
                      <TableCell>{instructor.name || "N/A"}</TableCell>
                      <TableCell>{instructor.email || instructor.InstWorkEmail || "N/A"}</TableCell>
                      <TableCell>{instructor.phone || "N/A"}</TableCell>
                      <TableCell>
                        {getSiteNameForInstructor(instructor)}
                      </TableCell>
                      <TableCell>
                        {getUserTypeLabels(instructor).join(", ")}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleEditClick(instructor)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {furtherFilteredArchived.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No archived instructors found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Grid container spacing={2} style={{ marginBottom: "50px" }}>
              {furtherFilteredArchived.map((instructor) =>
                renderInstructorCard(instructor, true),
              )}
              {furtherFilteredArchived.length === 0 && (
                <Grid item xs={12}>
                  <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="text.secondary">
                      No archived instructors found
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}

      {tabValue === 3 && <InstructorWelcomeEmailEditor />}

      {selectedInstructor && (tabValue === 0 || tabValue === 1) && (
        <EditTpInstructor
          open={editModalOpen}
          onClose={handleEditModalClose}
          instructor={selectedInstructor}
          onSuccess={handleEditSuccess}
          onUserDeleted={() => {
            fetchInstructors();
            setSnackbarMessage("Instructor has been deleted");
            setSnackbarOpen(true);
            setEditModalOpen(false);
          }}
        />
      )}

      {selectedInstructor && tabValue === 2 && (
        <EditArchivedInstructor
          open={editModalOpen}
          onClose={handleEditModalClose}
          instructor={selectedInstructor}
          onSuccess={handleEditSuccess}
          onUserDeleted={() => {
            fetchInstructors();
            setSnackbarMessage("Archived instructor has been deleted");
            setSnackbarOpen(true);
            setEditModalOpen(false);
          }}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%", backgroundColor: "black", color: "white" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <EmailUpdateDialog
        open={emailDialogOpen}
        onClose={handleEmailDialogClose}
        instructor={emailTargetInstructor}
        onSuccess={handleEmailUpdateSuccess}
      />

      <Dialog
        open={!!welcomeTarget}
        onClose={handleWelcomeClose}
        maxWidth="xs"
        fullWidth
      >
        <ModalDialogTitle onClose={handleWelcomeClose}>
          Send Welcome Email
        </ModalDialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to send a welcome email?
          </Typography>
          {welcomeTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {welcomeTarget.name || "Instructor"}
              {getInstructorEmail(welcomeTarget)
                ? ` (${getInstructorEmail(welcomeTarget)})`
                : ""}
            </Typography>
          )}
          {welcomeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {welcomeError}
            </Alert>
          )}
        </DialogContent>
        <ModalActions
          onSave={handleWelcomeConfirm}
          saving={welcomeSending}
          saveLabel="Send Email"
        />
      </Dialog>

      <ConfirmDeleteDialog
        open={bulkDeleteConfirmOpen}
        onClose={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={handleBulkDeleteConfirmed}
        deleting={bulkDeleting}
        title="Delete Selected Instructors"
        message={`Are you sure you want to delete ${selectedForDelete.length} selected instructor${
          selectedForDelete.length === 1 ? "" : "s"
        }? This permanently removes them from both the sign-in system and the users list. This action cannot be undone.`}
      />
      </Container>
    </Box>
  );
};

export default TpInstructors;
